import React from 'react';
import { FormControl, FormControlLabel, Checkbox, Box } from '@mui/material';
import useCustomizedDataEntrySectionsForTeacher from '../../../hooks/useCustomizedDataEntrySectionsForTeacher';
// Import the extracted section components
import ABCDataBehaviorSection from './ABCDataBehaviorSection';
import PrecedingBehaviorSection from '../../GoalData/DataEntryModal/components/PrecedingBehaviorSection';
import PhaseEventBehaviorSections from './PhaseEventBehaviorSections';
import TimeOfCollectionBehaviorSection from './TimeOfCollectionBehaviorSection';
import SettingLocationBehaviorSection from './SettingLocationBehaviorSection';
import PromptingLevelBehaviorSection from './PromptingLevelBehaviorSection';
import StaffPresentBehaviorSection from './StaffPresentBehaviorSection';
import CustomSectionsBehaviorWrapper from './CustomSectionsBehaviorWrapper';
// Import the extracted field components
import SessionSummaryField from './SessionSummaryField';
import GroupSizeField from './GroupSizeField';
import SessionLengthField from './SessionLengthField';
import CPTCodesField from './CPTCodesField';

const OptionalBehaviorFields = ({
  dataPoint,
  index,
  handleCheckboxChange,
  handleAbcChange,
  handleFieldChange,
  handleTimeChange,
  quicktrackInfo, // Add this new prop
  user, // Add user prop for custom sections
}) => {
  // Get the data entry selection from quicktrack info
  const rawDataEntrySelection =
    quicktrackInfo?.DataEntrySelection?.DataEntrySelection ??
    quicktrackInfo?.DataCollection?.DataEntrySelection?.DataEntrySelection ??
    quicktrackInfo?.info?.DataEntrySelection?.DataEntrySelection;

  const isSelectionConfigured =
    rawDataEntrySelection !== undefined && rawDataEntrySelection !== null;

  const dataEntrySelection = React.useMemo(() => {
    if (Array.isArray(rawDataEntrySelection)) {
      return rawDataEntrySelection;
    }

    if (
      rawDataEntrySelection &&
      typeof rawDataEntrySelection === 'object' &&
      !Array.isArray(rawDataEntrySelection)
    ) {
      return Object.entries(rawDataEntrySelection)
        .filter(([, value]) => Boolean(value))
        .map(([key]) => key);
    }

    if (rawDataEntrySelection) {
      return [rawDataEntrySelection];
    }

    return [];
  }, [rawDataEntrySelection]);

  // Helper function to check if an option should be shown
  const shouldShowOption = (option) => {
    // If no quicktrack info is provided, show all options (backward compatibility)
    if (!quicktrackInfo) {
      return true;
    }

    if (!isSelectionConfigured) {
      return true;
    }

    if (!dataEntrySelection.length) {
      return false;
    }

    // Map the option names to match the data entry selection
    const optionMapping = {
      'Add ABC Data': 'Add ABC Data',
      'Add Preceding Behavior': 'Add Preceding Behavior',
      'Add Staff Present Information': 'Add Staff Present Information',
      'Add Prompting Level': 'Add Prompting Level',
      'Add Phase Change Line': 'Add Phase Change Line',
      'Add Event Line': 'Add Event Line',
      'Add Time of Collection': 'Add Time of Collection',
      'Add Setting/Location': 'Add Setting/Location',
      'Add Session Summary': 'Add Session Summary',
      'Add Group Size': 'Add Group Size',
      'Add Session Length': 'Add Session Length',
      'Add CPT Code': 'Add CPT Code',
    };

    const mappedOption = optionMapping[option] || option;
    return dataEntrySelection.includes(mappedOption);
  };

  // Fetch all custom data entry sections for the school
  const { data: allCustomSections = [], isLoading: isLoadingCustomSections } =
    useCustomizedDataEntrySectionsForTeacher(user?.schoolId);

  // Handle custom section checkbox changes
  const handleCustomSectionCheckboxChange = (index, event) => {
    const value = event.target.value;
    const checked = event.target.checked;
    // Defensive: ensure selectedOptions is always an array
    const prevSelected = Array.isArray(dataPoint.selectedOptions)
      ? dataPoint.selectedOptions
      : [];
    let newSelected;
    if (checked) {
      // Add if not already present
      newSelected = prevSelected.includes(value)
        ? prevSelected
        : [...prevSelected, value];
    } else {
      // Remove if present
      newSelected = prevSelected.filter((v) => v !== value);

      // Also clear the custom section data when unchecking
      const currentCustomSection = dataPoint.customSection || [];
      const updatedCustomSection = currentCustomSection.filter(
        (section) => section.docId !== value
      );
      handleFieldChange(index, 'customSection', updatedCustomSection);
    }
    handleFieldChange(index, 'selectedOptions', newSelected);
  };

  // When answers inside a custom section change, update the corresponding field in the datapoint
  const handleCustomSectionAnswerChange = (updatedCustomSection) => {
    handleFieldChange(index, 'customSection', updatedCustomSection || []);
  };

  return (
    <FormControl
      component='fieldset'
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {/* ABC Data Section */}
      <ABCDataBehaviorSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleAbcChange={handleAbcChange}
        shouldShowOption={shouldShowOption}
      />

      {/* Preceding Behavior Section */}
      {shouldShowOption('Add Preceding Behavior') && (
        <PrecedingBehaviorSection
          dataPoint={dataPoint}
          index={index}
          handleCheckboxChange={handleCheckboxChange}
          handleFieldChange={handleFieldChange}
        />
      )}

      <PhaseEventBehaviorSections
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
        shouldShowOption={shouldShowOption}
      />

      {/* Time of Collection Section */}
      <TimeOfCollectionBehaviorSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
        handleTimeChange={handleTimeChange}
        quicktrackInfo={quicktrackInfo}
        shouldShowOption={shouldShowOption}
      />

      {/* Setting/Location Section */}
      <SettingLocationBehaviorSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
        shouldShowOption={shouldShowOption}
      />

      {/* Session Summary Section */}
      {shouldShowOption('Add Session Summary') && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={dataPoint.selectedOptions.includes(
                  'Add Session Summary'
                )}
                onChange={(e) => handleCheckboxChange(index, e)}
                value='Add Session Summary'
              />
            }
            label='Add Session Summary'
          />
          {dataPoint.selectedOptions.includes('Add Session Summary') && (
            <Box sx={{ ml: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              <SessionSummaryField
                dataPoint={dataPoint}
                index={index}
                handleFieldChange={handleFieldChange}
              />
            </Box>
          )}
        </>
      )}

      {/* Group Size Section */}
      {shouldShowOption('Add Group Size') && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={dataPoint.selectedOptions.includes('Add Group Size')}
                onChange={(e) => handleCheckboxChange(index, e)}
                value='Add Group Size'
              />
            }
            label='Add Group Size'
          />
          {dataPoint.selectedOptions.includes('Add Group Size') && (
            <Box sx={{ ml: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              <GroupSizeField
                dataPoint={dataPoint}
                index={index}
                handleFieldChange={handleFieldChange}
              />
            </Box>
          )}
        </>
      )}

      {/* Session Length Section */}
      {shouldShowOption('Add Session Length') && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={dataPoint.selectedOptions.includes(
                  'Add Session Length'
                )}
                onChange={(e) => handleCheckboxChange(index, e)}
                value='Add Session Length'
              />
            }
            label='Add Session Length'
          />
          {dataPoint.selectedOptions.includes('Add Session Length') && (
            <Box sx={{ ml: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              <SessionLengthField
                dataPoint={dataPoint}
                index={index}
                handleFieldChange={handleFieldChange}
              />
            </Box>
          )}
        </>
      )}

      {/* CPT Code Section */}
      {shouldShowOption('Add CPT Code') && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={dataPoint.selectedOptions.includes('Add CPT Code')}
                onChange={(e) => handleCheckboxChange(index, e)}
                value='Add CPT Code'
              />
            }
            label='Add CPT Code'
          />
          {dataPoint.selectedOptions.includes('Add CPT Code') && (
            <Box sx={{ ml: 3, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              <CPTCodesField
                dataPoint={dataPoint}
                index={index}
                handleFieldChange={handleFieldChange}
              />
            </Box>
          )}
        </>
      )}

      {/* Staff Present Information Section */}
      <StaffPresentBehaviorSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
        shouldShowOption={shouldShowOption}
      />

      {/* Prompting Level Section */}
      <PromptingLevelBehaviorSection
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleFieldChange={handleFieldChange}
        shouldShowOption={shouldShowOption}
      />

      {/* Custom Data Entry Sections */}
      <CustomSectionsBehaviorWrapper
        dataPoint={dataPoint}
        index={index}
        quicktrackInfo={quicktrackInfo}
        user={user}
        allCustomSections={allCustomSections}
        isLoadingCustomSections={isLoadingCustomSections}
        handleCustomSectionCheckboxChange={handleCustomSectionCheckboxChange}
        handleCustomSectionAnswerChange={handleCustomSectionAnswerChange}
      />
    </FormControl>
  );
};

export default OptionalBehaviorFields;
